// src/UsersDashboard.js
import React, { useEffect, useState, useMemo } from "react";
import { db } from "./firebase";
import { ref, onValue, get, set, remove } from "firebase/database"; // ✅ حذف update
import { useNavigate } from "react-router-dom";

export default function UsersDashboard() {
  const [users, setUsers] = useState([]);
  const [migrating, setMigrating] = useState(false);
  const [migrationStatus, setMigrationStatus] = useState("");
  const navigate = useNavigate();

  // 🔹 جلب البيانات
  useEffect(() => {
    const usersRef = ref(db, "users");
    const unsub = onValue(usersRef, (snap) => {
      const val = snap.val();
      if (!val) return setUsers([]);
      const arr = Object.keys(val).map((key) => ({
        id: key,
        ...val[key],
      }));
      setUsers(arr);
    });
    return () => unsub();
  }, []);

  // 🔹 حساب عدد المستخدمين حسب النوع
  const counts = useMemo(() => {
    const result = { client: 0, professional: 0, vendeur: 0, admin: 0 };
    users.forEach((u) => {
      if (u.userType && result[u.userType.toLowerCase()] !== undefined) {
        result[u.userType.toLowerCase()]++;
      }
    });
    return result;
  }, [users]);

  // 🔹 الانتقال لصفحة النوع المحدد
  const goToUserType = (type) => {
    navigate(`/users/${type.toLowerCase()}`);
  };

  // 🔹 دالة الترحيل المحسنة مع الحفاظ على الفصل بين clientApp و clientAdd
  const migrateClientsWithSeparation = async () => {
    if (!window.confirm("هل أنت متأكد من بدء عملية الترحيل؟ هذا سيحسن هيكل البيانات مع الحفاظ على فصل clientApp و clientAdd.")) {
      return;
    }

    setMigrating(true);
    setMigrationStatus("جاري قراءة البيانات...");

    try {
      // 1️⃣ قراءة جميع العملاء
      const clientsRef = ref(db, "clients");
      const clientsSnap = await get(clientsRef);
      const clients = clientsSnap.val() || {};
      
      // 2️⃣ قراءة جميع الطلبات من جميع الموزعين
      const distributorsRef = ref(db, "distributors");
      const distributorsSnap = await get(distributorsRef);
      const distributors = distributorsSnap.val() || {};

      // 3️⃣ بناء معلومات كاملة عن كل clientId
      const clientInfo = {};
      const phoneToClientIds = {};

      setMigrationStatus("تحليل العلاقات بين clientId والطلبات...");

      // المسح على كل الموزعين
      Object.keys(distributors).forEach(distributorId => {
        const orders = distributors[distributorId]?.orders || {};
        
        Object.keys(orders).forEach(orderId => {
          const order = orders[orderId];
          const clientId = order.clientId;
          
          if (!clientId) return;

          if (!clientInfo[clientId]) {
            clientInfo[clientId] = {
              type: null,
              phones: new Set(),
              distributors: new Set(),
              orders: [],
              hasTradeName: false,
              hasClientAdd: false
            };
          }

          if (order.tradeName && !order.nameClientAdd) {
            clientInfo[clientId].hasTradeName = true;
          }
          if (order.nameClientAdd && !order.tradeName) {
            clientInfo[clientId].hasClientAdd = true;
          }
          if (order.tradeName && order.nameClientAdd) {
            clientInfo[clientId].hasTradeName = true;
            clientInfo[clientId].hasClientAdd = true;
          }

          if (order.phone) {
            clientInfo[clientId].phones.add(order.phone);
            if (!phoneToClientIds[order.phone]) {
              phoneToClientIds[order.phone] = new Set();
            }
            phoneToClientIds[order.phone].add(clientId);
          }
          
          if (order.telClientAdd) {
            clientInfo[clientId].phones.add(order.telClientAdd);
            if (!phoneToClientIds[order.telClientAdd]) {
              phoneToClientIds[order.telClientAdd] = new Set();
            }
            phoneToClientIds[order.telClientAdd].add(clientId);
          }

          clientInfo[clientId].distributors.add(distributorId);
          clientInfo[clientId].orders.push({
            distributorId,
            orderId,
            ...order
          });
        });
      });

      Object.keys(clientInfo).forEach(clientId => {
        const info = clientInfo[clientId];
        if (info.hasTradeName && !info.hasClientAdd) {
          info.type = "app";
        } else if (!info.hasTradeName && info.hasClientAdd) {
          info.type = "add";
        } else if (info.hasTradeName && info.hasClientAdd) {
          info.type = "mixed";
        } else {
          info.type = "unknown";
        }
      });

      setMigrationStatus(`تم العثور على ${Object.keys(clientInfo).length} clientId`);

      let migrated = 0;
      let total = Object.keys(clients).filter(key => /^\d+$/.test(key)).length;

      for (const [phoneKey, clientData] of Object.entries(clients)) {
        if (!/^\d+$/.test(phoneKey)) continue;

        setMigrationStatus(`جاري ترحيل ${migrated + 1}/${total}: ${phoneKey}`);

        const relatedClientIds = phoneToClientIds[phoneKey] || new Set();
        
        if (relatedClientIds.size === 0) {
          const newClientId = crypto.randomUUID ? crypto.randomUUID() : 
                            'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                              const r = Math.random() * 16 | 0;
                              const v = c === 'x' ? r : ((r & 0x3) | 0x8); // ✅ تم التصحيح هنا
                              return v.toString(16);
                            });

          const newClientData = {
            ...clientData,
            phone: phoneKey,
            type: "legacy",
            _migrated: true,
            migratedAt: new Date().toISOString()
          };

          await set(ref(db, `clients/${newClientId}`), newClientData);
          await remove(ref(db, `clients/${phoneKey}`));
          
          console.log(`✅ تم إنشاء clientId جديد ${newClientId} للهاتف ${phoneKey}`);
          
        } else if (relatedClientIds.size === 1) {
          const targetClientId = Array.from(relatedClientIds)[0];
          const targetInfo = clientInfo[targetClientId];
          
          console.log(`📱 الهاتف ${phoneKey} مرتبط بـ clientId ${targetClientId} (نوع: ${targetInfo?.type})`);
          
          const targetRef = ref(db, `clients/${targetClientId}`);
          const targetSnap = await get(targetRef);
          const targetData = targetSnap.val() || {};

          const mergedData = {
            ...targetData,
            ...clientData,
            phone: phoneKey,
            type: targetInfo?.type || "unknown",
            _migrated: true,
            migratedAt: new Date().toISOString()
          };

          if (clientData.soldesParDistributeur || targetData.soldesParDistributeur) {
            const mergedSoldes = { ...(targetData.soldesParDistributeur || {}) };
            
            if (clientData.soldesParDistributeur) {
              Object.entries(clientData.soldesParDistributeur).forEach(([distId, solde]) => {
                if (mergedSoldes[distId]) {
                  mergedSoldes[distId] = (parseFloat(mergedSoldes[distId]) || 0) + (parseFloat(solde) || 0);
                } else {
                  mergedSoldes[distId] = solde;
                }
              });
            }
            
            mergedData.soldesParDistributeur = mergedSoldes;
          }

          await set(targetRef, mergedData);
          await remove(ref(db, `clients/${phoneKey}`));
          
          console.log(`✅ تم دمج الهاتف ${phoneKey} في clientId ${targetClientId}`);
          
        } else {
          console.log(`⚠️⚠️ الهاتف ${phoneKey} مرتبط بعدة clientId:`);
          Array.from(relatedClientIds).forEach(id => {
            console.log(`   - ${id} (نوع: ${clientInfo[id]?.type})`);
          });

          const newClientId = crypto.randomUUID ? crypto.randomUUID() : 
                            'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
                              const r = Math.random() * 16 | 0;
                              const v = c === 'x' ? r : ((r & 0x3) | 0x8); // ✅ تم التصحيح هنا
                              return v.toString(16);
                            });

          const newClientData = {
            ...clientData,
            phone: phoneKey,
            type: "legacy",
            _migrated: true,
            migratedAt: new Date().toISOString(),
            _note: "هذه بيانات قديمة، clientId الجديدة موجودة في الطلبات",
            relatedClientIds: Array.from(relatedClientIds)
          };

          await set(ref(db, `clients/${newClientId}`), newClientData);
          await remove(ref(db, `clients/${phoneKey}`));
          
          console.log(`✅ تم إنشاء clientId جديد ${newClientId} للبيانات القديمة`);
        }

        migrated++;
      }

      setMigrationStatus(`✅ تم الترحيل بنجاح! ${migrated} عميل تم ترحيلهم مع الحفاظ على الفصل بين clientApp و clientAdd.`);

    } catch (error) {
      console.error("❌ خطأ في الترحيل:", error);
      setMigrationStatus(`❌ فشل الترحيل: ${error.message}`);
    } finally {
      setMigrating(false);
    }
  };

  return (
    <div style={{ padding: 20, background: "white", borderRadius: 8 }}>
      <h3>Users Management Dashboard</h3>

      <div style={{ display: "flex", gap: 20, marginBottom: 20, flexWrap: "wrap" }}>
        <Card label="Clients" count={counts.client} color="#4f46e5" onClick={() => goToUserType("client")} />
        <Card label="Professionals" count={counts.professional} color="#0891b2" onClick={() => goToUserType("professional")} />
        <Card label="Vendeurs" count={counts.vendeur} color="#16a34a" onClick={() => goToUserType("vendeur")} />
        <Card label="Admins" count={counts.admin} color="#b91c1c" onClick={() => goToUserType("admin")} />
      </div>

      <div style={{ background: "#f8fafc", padding: 15, borderRadius: 8, marginBottom: 30 }}>
        <h4 style={{ marginTop: 0 }}>Quick Summary</h4>
        <div style={{ display: "flex", gap: 30, flexWrap: "wrap" }}>
          <div><strong>Total Users:</strong> {users.length}</div>
          <div><strong>Active Users:</strong> {users.filter(u => u.active !== false).length}</div>
          <div><strong>Inactive Users:</strong> {users.filter(u => u.active === false).length}</div>
          <div><strong>Last Updated:</strong> {new Date().toLocaleTimeString()}</div>
        </div>
      </div>

      {migrationStatus && (
        <div style={{
          padding: 15,
          marginBottom: 20,
          background: migrationStatus.includes("✅") ? "#d4edda" : migrationStatus.includes("❌") ? "#f8d7da" : "#fff3cd",
          color: migrationStatus.includes("✅") ? "#155724" : migrationStatus.includes("❌") ? "#721c24" : "#856404",
          borderRadius: 8,
          border: `1px solid ${migrationStatus.includes("✅") ? "#c3e6cb" : migrationStatus.includes("❌") ? "#f5c6cb" : "#ffeeba"}`
        }}>
          {migrationStatus}
          {migrating && <span style={{ marginLeft: 10 }}>⏳</span>}
        </div>
      )}

      <div style={{ display: "flex", gap: 15, justifyContent: "center", flexWrap: "wrap", marginTop: 30 }}>
        <button onClick={() => navigate("/users/all")} style={{ padding: "12px 24px", background: "#3b82f6", color: "white", border: "none", borderRadius: 8, fontSize: "16px", cursor: "pointer", minWidth: 200 }}>
          📋 View All Users
        </button>
        
        <button onClick={migrateClientsWithSeparation} disabled={migrating} style={{ padding: "12px 24px", background: migrating ? "#9ca3af" : "#059669", color: "white", border: "none", borderRadius: 8, fontSize: "16px", cursor: migrating ? "not-allowed" : "pointer", minWidth: 200 }}>
          {migrating ? "جاري الترحيل..." : "🔄 ترحيل مع الفصل (clientApp/clientAdd)"}
        </button>
        
        <button onClick={() => navigate("/")} style={{ padding: "12px 24px", background: "#6b7280", color: "white", border: "none", borderRadius: 8, fontSize: "16px", cursor: "pointer", minWidth: 200 }}>
          ← Back to Main
        </button>
      </div>

      <div style={{ marginTop: 30, padding: 15, background: "#f0f9ff", borderRadius: 8 }}>
        <h4 style={{ marginTop: 0 }}>Quick Links</h4>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {["client", "professional", "vendeur", "admin"].map((type) => (
            <button key={type} onClick={() => goToUserType(type)} style={{ padding: "8px 16px", background: "#dbeafe", color: "#1e40af", border: "1px solid #93c5fd", borderRadius: 6, cursor: "pointer", fontSize: "14px" }}>
              View {type.charAt(0).toUpperCase() + type.slice(1)}s
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function Card({ label, count, color, onClick }) {
  return (
    <div onClick={onClick} style={{ background: color, color: "#fff", borderRadius: 8, padding: "15px", flex: "1 1 200px", textAlign: "center", cursor: "pointer", transition: "all 0.3s ease", boxShadow: "0 4px 6px rgba(0,0,0,0.1)", minHeight: 120, display: "flex", flexDirection: "column", justifyContent: "center" }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-5px)"; e.currentTarget.style.boxShadow = "0 8px 12px rgba(0,0,0,0.15)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)"; }}
    >
      <h4 style={{ margin: 0, fontSize: "18px" }}>{label}</h4>
      <p style={{ fontSize: 32, fontWeight: "bold", margin: "10px 0 5px 0" }}>{count}</p>
      <small style={{ opacity: 0.9, fontSize: "12px" }}>Click to view details</small>
    </div>
  );
}